import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  paidMediaOverviewService,
  type PaidMediaOverviewCampaignBenchmarks,
  type PaidMediaOverviewCampaignRow,
  type PaidMediaOverviewCampaignsParams,
  type PaidMediaOverviewPlatformFilter,
  type PaidMediaOverviewSortOrder,
  type PaidMediaOverviewWarning,
} from "src/services/PaidMediaOverviewAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { useAsyncResource } from "src/hooks/UseAsyncResource";

const EXPORT_PAGE_SIZE = 100;
const EXPORT_PAGE_LIMIT = 50;

export interface UsePaidMediaOverviewCampaignsParams {
  startDate: string;
  endDate: string;
  platform: PaidMediaOverviewPlatformFilter;
  campaignId: string;
  objectiveOrNetwork: string;
  status: string;
  search: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: PaidMediaOverviewSortOrder;
}

export interface PaidMediaOverviewCampaignsBundle {
  rows: PaidMediaOverviewCampaignRow[];
  benchmarks: PaidMediaOverviewCampaignBenchmarks | null;
  currency: string | null;
  page: number;
  totalPages: number;
  totalRows: number;
  warnings: PaidMediaOverviewWarning[];
  loading: boolean;
  error: string | null;
  exporting: boolean;
  fetchAllRows: () => Promise<PaidMediaOverviewCampaignRow[]>;
}

export function usePaidMediaOverviewCampaigns({
  startDate,
  endDate,
  platform,
  campaignId,
  objectiveOrNetwork,
  status,
  search,
  page,
  pageSize,
  sortBy,
  sortOrder,
}: UsePaidMediaOverviewCampaignsParams): PaidMediaOverviewCampaignsBundle {
  const { t, i18n } = useTranslation();
  const fallbackError = t("paidMediaOverview.errors.requestFailed");
  const [exporting, setExporting] = useState(false);
  // `status_label` and `status_reason` come back localized, so the rows (and the CSV export that
  // reuses these params) have to be re-fetched when the language changes.
  const lang = getApiLangFromI18n(i18n);

  const baseParams = useMemo<PaidMediaOverviewCampaignsParams>(
    () => ({
      start_date: startDate,
      end_date: endDate,
      platform,
      campaign_id: campaignId || undefined,
      objective_or_network: objectiveOrNetwork || undefined,
      status: status || undefined,
      search: search.trim() || undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
      lang,
    }),
    [
      lang,
      startDate,
      endDate,
      platform,
      campaignId,
      objectiveOrNetwork,
      status,
      search,
      sortBy,
      sortOrder,
    ]
  );

  const request = useCallback(
    () =>
      paidMediaOverviewService.fetchCampaigns({
        ...baseParams,
        page,
        page_size: pageSize,
      }),
    [baseParams, page, pageSize]
  );

  const campaigns = useAsyncResource(
    startDate && endDate ? request : null,
    fallbackError
  );

  const fetchAllRows = useCallback(async () => {
    setExporting(true);
    try {
      const collected: PaidMediaOverviewCampaignRow[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await paidMediaOverviewService.fetchCampaigns({
          ...baseParams,
          page: currentPage,
          page_size: EXPORT_PAGE_SIZE,
        });
        collected.push(...(response?.data?.rows ?? []));
        totalPages = Math.min(response?.data?.total_pages ?? 1, EXPORT_PAGE_LIMIT);
        currentPage += 1;
      } while (currentPage <= totalPages);

      return collected;
    } finally {
      setExporting(false);
    }
  }, [baseParams]);

  const data = campaigns.data?.data ?? null;

  return {
    rows: data?.rows ?? [],
    benchmarks: data?.benchmarks ?? null,
    currency: campaigns.data?.meta?.currency ?? null,
    page: data?.page ?? page,
    totalPages: Math.max(1, data?.total_pages ?? 1),
    totalRows: data?.total_rows ?? 0,
    warnings: campaigns.data?.meta?.warnings ?? [],
    loading: campaigns.loading,
    error: campaigns.error,
    exporting,
    fetchAllRows,
  };
}
